import { Component, Input, OnInit, Output, EventEmitter, ChangeDetectionStrategy } from "@angular/core";

interface Column {
  label: string;
  value: string;
  type?: "string" | "number";
}

@Component({
    selector: "app-data-table",
    templateUrl: "./data-table.component.html",
    styleUrls: ["./data-table.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DataTableComponent implements OnInit {
  @Input() data: any[] = [];
  @Input() columns: Column[] = [];
  @Input() showActions: boolean = true;
  @Input() disableActions: boolean = false;

  @Output() editRow = new EventEmitter<any>();
  @Output() deleteRow = new EventEmitter<any>();

  sortedData: any[] = [];
  sortColumn: string | null = null;
  sortDirection: "asc" | "desc" | null = null;

  ngOnInit() {
    this.sortedData = [...this.data];
    if (!this.columns.length && this.data.length > 0) {
      // If columns are not provided, create default columns from data keys
      this.columns = Object.keys(this.data[0]).map((key) => ({
        label: key,
        value: key,
        type: "string",
      }));
    }
  }

  sortBy(column: Column) {
    if (this.sortColumn === column.value) {
      this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
    } else {
      this.sortColumn = column.value;
      this.sortDirection = "asc";
    }

    this.sortedData.sort((a, b) => {
      let valA = a[column.value];
      let valB = b[column.value];

      // Handle null/undefined values
      if (valA === null || valA === undefined)
        valA = column.type === "number" ? -Infinity : "";
      if (valB === null || valB === undefined)
        valB = column.type === "number" ? -Infinity : "";

      // Perform comparison based on column type
      let compare: number;
      if (column.type === "number") {
        // Convert to numbers for numeric sorting
        valA = Number(valA);
        valB = Number(valB);

        // Handle NaN values
        if (isNaN(valA)) valA = -Infinity;
        if (isNaN(valB)) valB = -Infinity;

        compare = valA - valB; // Numeric comparison
      } else {
        // String comparison (case-insensitive)
        const strA = String(valA).toLowerCase();
        const strB = String(valB).toLowerCase();
        compare = strA < strB ? -1 : strA > strB ? 1 : 0;
      }

      return this.sortDirection === "asc" ? compare : -compare;
    });
  }

  isSortedAsc(column: Column): boolean {
    return this.sortColumn === column.value && this.sortDirection === "asc";
  }

  isSortedDesc(column: Column): boolean {
    return this.sortColumn === column.value && this.sortDirection === "desc";
  }

  onEdit(row: any) {
    this.editRow.emit(row);
  }

  onDelete(row: any) {
    this.deleteRow.emit(row);
  }
}
